import React, { useEffect, useRef, useState } from "react";
import "./LanguageToggle.scss";
import languageIcon from "assets/images/preferred-language.png";
import languageIconDisabled from "assets/images/preferred-language-disabled.png";
import languageLockIcon from "assets/images/preferred-language-lock.png";
import tickIcon from "assets/images/v2/tick-mark.svg";
import chevronDownIcon from "assets/images/v2/chevron-down.svg";

interface LanguageToggleProps {
    value: string;
    onChange: (value: string) => void;
    options: string[];
    isMobile?: boolean;
    disabled?: boolean;
    onLockedClick?: () => void;
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({ value, onChange, options, isMobile = false, disabled = false, onLockedClick }: LanguageToggleProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const userLanguage = value;
    const isMobileLockedAction = isMobile && disabled && Boolean(onLockedClick);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!dropdownRef.current) {
                return;
            }

            if (event.target instanceof Node && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleOptionClick = (language: string) => {
        if (disabled) {
            return;
        }

        onChange(language);
        setIsOpen(false);
    };

    const toggleOptions = () => {
        if (isMobileLockedAction) {
            onLockedClick?.();
            return;
        }

        if (disabled) {
            return;
        }

        setIsOpen((prev) => !prev);
    };

    const optionsClassName = isMobile
        ? "preferred-language__options preferred-language__options--mobile"
        : "preferred-language__options preferred-language__options--desktop";

    const renderOptions = () => (
        <ul className={optionsClassName} aria-label="Select preferred language">
            {options.map((language) => (
                <li key={language}>
                    <button
                        type="button"
                        className="preferred-language__option"
                        aria-pressed={userLanguage === language}
                        onClick={() => handleOptionClick(language)}
                    >
                        <span className="preferred-language__option-label GlobalLinkNoTx">{language}</span>
                        {userLanguage === language && <img src={tickIcon} alt="Selected" className="preferred-language__tick" />}
                    </button>
                </li>
            ))}
        </ul>
    );

    if (isMobile) {
        return (
            <div className="preferred-language__mobile-wrapper" ref={dropdownRef}>
                <button
                    type="button"
                    className="preferred-language__icon-button"
                    aria-label={isMobileLockedAction ? "Learn more about locked language preference" : "Toggle language options"}
                    aria-disabled={disabled && !isMobileLockedAction ? "true" : undefined}
                    aria-expanded={isOpen}
                    disabled={disabled && !isMobileLockedAction}
                    onClick={toggleOptions}
                >
                    {disabled ? (
                        <span className="preferred-language__icon-wrapper">
                            <img src={languageIcon} alt="Preferred language" className="preferred-language__mobile-image preferred-language__mobile-image--large" />
                            <img src={languageLockIcon} alt="" className="preferred-language__lock-icon" aria-hidden="true" />
                        </span>
                    ) : (
                        <img src={languageIcon} alt="Preferred language" className="preferred-language__mobile-image preferred-language__mobile-image--large" />
                    )}
                </button>

                {isOpen && !disabled && renderOptions()}
            </div>
        );
    }

    return (
        <div className="preferred-language__toggle" ref={dropdownRef}>
            {disabled ? (
                <button
                    type="button"
                    className="preferred-language__desktop-trigger preferred-language__desktop-trigger--disabled"
                    disabled
                    aria-label="Preferred language"
                    aria-disabled="true"
                >
                    <span className="preferred-language__icon-wrapper">
                        <img src={languageIconDisabled} alt="Preferred language" className="preferred-language__mobile-image preferred-language__mobile-image--large" />
                        <img src={languageLockIcon} alt="" className="preferred-language__lock-icon" aria-hidden="true" />
                    </span>
                    <span className="preferred-language__selected-label GlobalLinkNoTx">{userLanguage}</span>
                    <img src={chevronDownIcon} alt="" className="preferred-language__chevron" />
                </button>
            ) : (
                <button
                    type="button"
                    className="preferred-language__desktop-trigger"
                    aria-label="Toggle language options"
                    aria-expanded={isOpen}
                    onClick={toggleOptions}
                >
                    <img src={languageIcon} alt="Preferred language" className="preferred-language__mobile-image" />
                    <span className="preferred-language__selected-label GlobalLinkNoTx">{userLanguage}</span>
                    <img src={chevronDownIcon} alt="" className="preferred-language__chevron" />
                </button>
            )}

            {isOpen && !disabled && renderOptions()}
        </div>
    );
};

export default LanguageToggle;
